import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { AltalabLogo } from "../components/AltalabLogo";
import { useAuth } from "../contexts/AuthContext";
import BottomNavigation from "../components/BottomNavigation";
import axios from "axios";
import {
  Users,
  ShoppingBag,
  Star,
  Shield,
  Search,
  Filter,
  Instagram,
  Twitter,
  ChevronDown,
  Loader2,
  ArrowLeft,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform icons mapping
const PlatformIcon = ({ platform, className = "w-5 h-5" }) => {
  const icons = {
    telegram: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
      </svg>
    ),
    privacy: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" fill="none"/>
      </svg>
    ),
    onlyfans: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.418 0-8-3.582-8-8s3.582-8 8-8 8 3.582 8 8-3.582 8-8 8zm0-14c-3.314 0-6 2.686-6 6s2.686 6 6 6 6-2.686 6-6-2.686-6-6-6z"/>
      </svg>
    ),
    twitter: <Twitter className={className} />,
    instagram: <Instagram className={className} />,
    tiktok: (
      <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    ),
  };
  return icons[platform] || <ShoppingBag className={className} />;
};

const PLATFORMS = [
  { value: "", label: "Todas Plataformas" },
  { value: "telegram", label: "Telegram" },
  { value: "privacy", label: "Privacy" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "twitter", label: "X / Twitter" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
];

const PRICE_RANGES = [
  { value: "", label: "Qualquer Preço" },
  { value: "0-100", label: "Até R$ 100" },
  { value: "100-300", label: "R$ 100 - R$ 300" },
  { value: "300-500", label: "R$ 300 - R$ 500" },
  { value: "500-", label: "Acima de R$ 500" },
];

const ServiceCard = ({ service, onClick }) => {
  const getPhoto = () => {
    if (service.seller?.profile_photo) {
      if (service.seller.profile_photo.startsWith('http')) {
        return service.seller.profile_photo;
      }
      return `${process.env.REACT_APP_BACKEND_URL}${service.seller.profile_photo}`;
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -5 }}
      onClick={onClick}
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 cursor-pointer hover:border-[#FD5564]/50 transition-all"
      data-testid={`service-card-${service.id}`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
          {getPhoto() ? (
            <img src={getPhoto()} alt={service.seller?.display_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/30">
              <Users className="w-6 h-6" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">{service.seller?.display_name}</span>
            {service.seller?.is_verified && (
              <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            )}
          </div>
          {service.seller?.trust_score > 0 && (
            <div className="flex items-center gap-1 text-yellow-400 text-sm">
              <Star className="w-3 h-3 fill-current" />
              <span>{service.seller.trust_score.toFixed(1)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg bg-[#FD5564]/20 text-[#FD5564]">
          <PlatformIcon platform={service.platform} className="w-4 h-4" />
        </div>
        <span className="text-sm text-white/70 uppercase tracking-wide">{service.platform}</span>
      </div>

      <h3 className="font-semibold text-white mb-1 line-clamp-1">{service.title}</h3>
      <p className="text-sm text-white/50 mb-3">{service.reach}</p>

      <div className="flex items-center justify-between">
        <span className="text-xl font-bold text-[#FD5564]">
          R$ {service.price?.toFixed(2).replace('.', ',')}
        </span>
        <Button size="sm" className="bg-[#FD5564] hover:bg-[#FF7854] text-white">
          Ver
        </Button>
      </div>
    </motion.div>
  );
};

const MarketplacePage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedPriceRange, setSelectedPriceRange] = useState("");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchServices();
  }, [selectedPlatform, selectedPriceRange]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      let url = `${API}/services/public?limit=50`;
      
      if (selectedPlatform) {
        url += `&platform=${selectedPlatform}`;
      }
      
      if (selectedPriceRange) {
        const [min, max] = selectedPriceRange.split('-');
        if (min) url += `&min_price=${min}`;
        if (max) url += `&max_price=${max}`;
      }
      
      const response = await axios.get(url);
      setServices(response.data.services || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch services:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredServices = services.filter(service => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      service.title?.toLowerCase().includes(query) ||
      service.seller?.display_name?.toLowerCase().includes(query) ||
      service.platform?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <AltalabLogo size="default" />
          </div>
          <div className="flex items-center gap-3">
            {user ? (
              <Button
                className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                onClick={() => navigate("/discovery")}
              >
                Minha Conta
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  className="text-white/70 hover:text-white"
                  onClick={() => navigate("/auth")}
                >
                  Entrar
                </Button>
                <Button
                  className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90 text-white"
                  onClick={() => navigate("/auth?register=true")}
                >
                  Criar Conta
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Divulgação Paga</h1>
            <p className="text-white/50">Encontre os melhores serviços para divulgar seu conteúdo</p>
          </div>

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
              <Input
                placeholder="Buscar serviços..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="search-input"
              />
            </div>

            {/* Platform Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-white/10 text-white bg-white/5 min-w-[180px] justify-between">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    {PLATFORMS.find(p => p.value === selectedPlatform)?.label || "Plataforma"}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1a1a] border-white/10">
                {PLATFORMS.map((platform) => (
                  <DropdownMenuItem
                    key={platform.value}
                    onClick={() => setSelectedPlatform(platform.value)}
                    className="text-white hover:bg-white/10 cursor-pointer"
                  >
                    {platform.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Price Filter */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-white/10 text-white bg-white/5 min-w-[180px] justify-between">
                  <span>{PRICE_RANGES.find(p => p.value === selectedPriceRange)?.label || "Preço"}</span>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1a1a1a] border-white/10">
                {PRICE_RANGES.map((range) => (
                  <DropdownMenuItem
                    key={range.value}
                    onClick={() => setSelectedPriceRange(range.value)}
                    className="text-white hover:bg-white/10 cursor-pointer"
                  >
                    {range.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Results count */}
          <div className="mb-6">
            <span className="text-white/50">{filteredServices.length} serviços encontrados</span>
          </div>

          {/* Services Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
            </div>
          ) : filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  onClick={() => navigate(`/s/${service.id}`)}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <ShoppingBag className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50 mb-4">Nenhum serviço encontrado</p>
              {user ? (
                <Button
                  className="bg-[#FD5564] hover:bg-[#FF7854]"
                  onClick={() => navigate("/profile")}
                >
                  Criar Meu Primeiro Serviço
                </Button>
              ) : (
                <Button
                  className="bg-[#FD5564] hover:bg-[#FF7854]"
                  onClick={() => navigate("/auth?register=true")}
                >
                  Criar Conta para Anunciar
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Bottom Navigation - only show if logged in */}
      {token && <BottomNavigation />}
    </div>
  );
};

export default MarketplacePage;
